import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Box } from '../Box/index.js';
import { Stack } from '../Stack/index.js';
import { Icon } from '../Icon/index.js';
import { useInputGroupContext } from '../InputGroup/index.js';

/* ======================================= */
const CheckboxWrapper = styled(Box).withConfig({
  displayName: "Checkbox__CheckboxWrapper",
  componentId: "sc-h63rw4-0"
})(["position:relative;width:fit-content;cursor:", ";"], _ref => {
  let {
    isDisabled
  } = _ref;
  return isDisabled ? 'not-allowed' : 'pointer';
});
const FakeCheckbox = styled(Box).withConfig({
  shouldForwardProp: prop => {
    return !['colorVariant'].includes(prop);
  }
}).withConfig({
  displayName: "Checkbox__FakeCheckbox",
  componentId: "sc-h63rw4-1"
})(["border:2px solid;border-radius:2px;border-color:", ";width:18px;height:18px;display:inline-flex;vertical-align:middle;align-items:center;justify-content:center;color:transparent;-webkit-tap-highlight-color:rgba(0,0,0,0);transition-property:background-color,box-shadow;"], _ref2 => {
  let {
    isInvalid,
    colorVariant
  } = _ref2;
  return isInvalid ? themeGet('colors.inputAlert') : themeGet(`colors.selectors.${colorVariant}.offDefault`);
});
const HiddenCheckbox = styled(Box).withConfig({
  shouldForwardProp: prop => {
    return !['colorVariant'].includes(prop);
  }
}).withConfig({
  displayName: "Checkbox__HiddenCheckbox",
  componentId: "sc-h63rw4-2"
})(["appearance:none;cursor:pointer;position:absolute;top:0;left:0;width:100%;height:100%;z-index:0;outline:none;margin:0;opacity:0;-webkit-tap-highlight-color:rgba(0,0,0,0);&:disabled{cursor:not-allowed;}&:hover:not(:disabled):not(:checked) + ", ",&:focus:not(:disabled):not(:checked) + ", "{border-color:", ";}&:disabled + ", "{border-color:", ";cursor:not-allowed;}&:focus + ", "{box-shadow:", ";border-color:", ";}&:checked + ", ",&:indeterminate + ", "{background-color:", ";border-color:", ";color:", ";}&:checked:disabled + ", "{background-color:", ";border-color:", ";color:", ";}&:checked:hover:not(:disabled) + ", ",&:checked:focus + ", "{background-color:", ";border-color:", ";color:", ";}"], FakeCheckbox, FakeCheckbox, _ref3 => {
  let {
    isInvalid,
    colorVariant
  } = _ref3;
  return isInvalid ? themeGet(`colors.selectors.${colorVariant}.alert`) : themeGet(`colors.selectors.${colorVariant}.offFocus`);
}, FakeCheckbox, _ref4 => {
  let {
    colorVariant
  } = _ref4;
  return themeGet(`colors.selectors.${colorVariant}.offDisabled`);
}, FakeCheckbox, _ref5 => {
  let {
    isInvalid
  } = _ref5;
  return isInvalid ? themeGet('shadows.error') : themeGet('shadows.focus');
}, _ref6 => {
  let {
    colorVariant
  } = _ref6;
  return themeGet(`colors.selectors.${colorVariant}offHover`);
}, FakeCheckbox, FakeCheckbox, _ref7 => {
  let {
    colorVariant
  } = _ref7;
  return themeGet(`colors.selectors.${colorVariant}.onDefault`);
}, _ref8 => {
  let {
    colorVariant
  } = _ref8;
  return themeGet(`colors.selectors.${colorVariant}.onDefault`);
}, themeGet('colors.white'), FakeCheckbox, _ref9 => {
  let {
    colorVariant
  } = _ref9;
  return themeGet(`colors.selectors.${colorVariant}.onDisabled`);
}, _ref10 => {
  let {
    colorVariant
  } = _ref10;
  return themeGet(`colors.selectors.${colorVariant}.onDisabled`);
}, themeGet('colors.white'), FakeCheckbox, FakeCheckbox, _ref11 => {
  let {
    colorVariant
  } = _ref11;
  return themeGet(`colors.selectors.${colorVariant}.onFocus`);
}, _ref12 => {
  let {
    colorVariant
  } = _ref12;
  return themeGet(`colors.selectors.${colorVariant}.onFocus`);
}, themeGet('colors.white'));
const TextWrapper = styled('span').withConfig({
  displayName: "Checkbox__TextWrapper",
  componentId: "sc-h63rw4-3"
})(["font-size:", ";line-height:", ";margin-left:", ";margin-top:-2px;user-select:none;vertical-align:middle;color:", ";"], themeGet('fontSizes.fontSize20'), themeGet('lineHeights.lineHeight20'), themeGet('space.space30'), _ref13 => {
  let {
    isDisabled
  } = _ref13;
  return isDisabled && themeGet(`colors.inputDisabled`);
});
/* ======================================= */
/**
 * CheckboxGroup wraps multiple checkboxes for accessibility.
 *
 * CheckboxGroup extends from Stack with the default `space40` spacing between each element.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/checkbox#checkboxgroup)
 * - [Props](https://blockparty.endpoint.com/components/checkbox#props)
 * <br />
 *
 */
const CheckboxGroup = /*#__PURE__*/forwardRef((_ref14, forwardedRef) => {
  let {
    children,
    role = 'group',
    id,
    ...props
  } = _ref14;
  return /*#__PURE__*/React__default.createElement(Stack, _extends({
    ref: forwardedRef,
    id: id,
    role: role
  }, props), children);
});
/* ======================================= */
/**
 * Checkbox component is used in forms when a user needs to
 * select multiple values from several options.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/checkbox#usage)
 * - [Props](https://blockparty.endpoint.com/components/checkbox#props)
 * <br />
 *
 */
const Checkbox = /*#__PURE__*/forwardRef((_ref15, forwardedRef) => {
  let {
    children,
    colorVariant = 'default',
    value,
    dataTestId = 'checkbox',
    name,
    onChange,
    isChecked = false,
    isReadOnly,
    isInvalid,
    isDisabled,
    isRequired,
    isIndeterminate,
    ...props
  } = _ref15;
  // Used for indeterminate state, i.e. Table with row selections
  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);
  const [checked, setChecked] = useState(isChecked);
  const formControl = useInputGroupContext();
  const _isDisabled = isDisabled || formControl.isDisabled;
  const _isInvalid = isInvalid || formControl.isInvalid;
  const _isRequired = isRequired || formControl.isRequired;
  const _isReadOnly = isReadOnly || formControl.isReadOnly;
  return /*#__PURE__*/React__default.createElement(CheckboxWrapper, _extends({
    as: "label",
    display: "flex",
    isDisabled: isDisabled
  }, props), /*#__PURE__*/React__default.createElement(HiddenCheckbox, {
    ref: forwardedRef,
    "aria-checked": isIndeterminate ? 'mixed' : checked,
    "aria-invalid": _isInvalid,
    "aria-readonly": _isReadOnly,
    "aria-required": _isRequired,
    as: "input",
    checked: checked,
    colorVariant: colorVariant,
    "data-test-id": dataTestId,
    disabled: _isDisabled,
    isInvalid: _isInvalid,
    name: name,
    required: _isRequired,
    type: "checkbox",
    value: value,
    onChange: e => {
      setChecked(!checked);
      onChange && onChange(e);
    }
  }), /*#__PURE__*/React__default.createElement(FakeCheckbox, {
    colorVariant: colorVariant,
    isInvalid: _isInvalid,
    transition: "fast"
  }, /*#__PURE__*/React__default.createElement(Icon, {
    name: isIndeterminate ? 'Minus' : 'CheckBasic'
  })), children && /*#__PURE__*/React__default.createElement(TextWrapper, {
    isDisabled: _isDisabled
  }, children));
});

export { Checkbox, CheckboxGroup };
